import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";

import {
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
  mergeAppearance,
} from "./utils";

import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

// Base das ferramentas de níveis derivados de um intervalo de tempo
// (AverageLine / QuadrantLines / TironeLevels): os arquivos de tool eram
// gêmeos 100% mecânicos — a matemática (média, quartis, terços) vive nos
// wrappers Each* (auditoria 2026-07-17, item 1d; família de fixes replicados
// à mão #330/#361/#362/#363). Cada tool vira subclasse de config com
// estáticos { collectionKey, EachComponent, evoId } + defaultProps próprios
// (hoverText). O desenho persiste só { x1, x2 } (intervalo de tempo).
class DerivedLevelsTool extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawLine = this.handleDrawLine.bind(this);
    this.handleDragLine = this.handleDragLine.bind(this);
    this.handleDragLineComplete = this.handleDragLineComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType(
      this.constructor.collectionKey,
    ).bind(this);

    this.state = {
      current: null,
      override: null,
    };
    this.nodes = [];
  }

  handleDragLine(index, newXYValue) {
    this.setState({
      override: {
        index,
        ...newXYValue,
      },
    });
  }

  handleDragLineComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const objects = this.props[this.constructor.collectionKey];
      const newObjects = objects.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              x1: override.x1,
              x2: override.x2,
              selected: true,
            }
          : {
              ...each,
              selected: false,
            },
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newObjects, moreProps);
        },
      );
    }
  }

  handleDrawLine(xyValue) {
    const { current } = this.state;
    if (isDefined(current) && isDefined(current.x1)) {
      this.mouseMoved = true;
      this.setState({
        current: {
          x1: current.x1,
          x2: xyValue[0], // Only update time (x), not price (y)
        },
      });
    }
  }

  handleStart(xyValue, moreProps, e) {
    const { current } = this.state;

    if (isNotDefined(current) || isNotDefined(current.x1)) {
      this.mouseMoved = false;

      this.setState(
        {
          current: {
            x1: xyValue[0], // Start time
            x2: null,
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    }
  }

  handleEnd(xyValue, moreProps, e) {
    const { current } = this.state;
    const { appearance } = this.props;
    const objects = this.props[this.constructor.collectionKey];

    if (this.mouseMoved && isDefined(current) && isDefined(current.x1)) {
      const currentAppearance = appearance;

      const newObjects = [
        ...objects.map((d) => ({ ...d, selected: false })),
        {
          x1: current.x1,
          x2: xyValue[0], // End time
          selected: true,
          appearance: currentAppearance,
        },
      ];

      this.setState(
        {
          current: null,
        },
        () => {
          this.props.onComplete(newObjects, moreProps, e);
        },
      );
    }
  }

  render() {
    const { appearance } = this.props;
    const { enabled, chartSectionId } = this.props;
    const { currentPositionRadius, currentPositionStroke } = this.props;
    const { currentPositionStrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { hoverText } = this.props;
    const objects = this.props[this.constructor.collectionKey];
    const { current, override } = this.state;
    const { EachComponent, evoId } = this.constructor;

    const hoverTextWithDefaults = {
      ...this.constructor.defaultProps.hoverText,
      ...hoverText,
    };

    return (
      <g>
        {objects.map((each, idx) => {
          // Properly merge appearance objects
          const eachAppearance = mergeAppearance(appearance, each.appearance);

          return (
            <EachComponent
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              {...(idx === override?.index ? override : each)}
              appearance={eachAppearance}
              hoverText={hoverTextWithDefaults}
              onDrag={this.handleDragLine}
              onDragComplete={this.handleDragLineComplete}
              chartSectionId={chartSectionId}
            />
          );
        })}

        {/* Preview line during drawing */}
        {isDefined(current) && isDefined(current.x2) && (
          <EachComponent
            interactive={false}
            x1={current.x1}
            x2={current.x2}
            appearance={appearance}
            hoverText={hoverTextWithDefaults}
          />
        )}

        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionStrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onClick={this.handleEnd}
          onMouseMove={this.handleDrawLine}
          data-evo-id={evoId}
        />
      </g>
    );
  }
}

// propTypes comuns — cada subclasse acrescenta a prop de coleção
export const derivedLevelsPropTypes = {
  enabled: PropTypes.bool.isRequired,
  chartSectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  onStart: PropTypes.func,
  onComplete: PropTypes.func,
  onSelect: PropTypes.func,

  hoverText: PropTypes.object,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionStrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,

  appearance: PropTypes.shape({
    stroke: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
      PropTypes.string,
    ]),
  }).isRequired,
};

// defaults comuns — cada subclasse espalha, define a coleção vazia e o
// hoverText com o texto próprio
export const derivedLevelsBaseDefaults = {
  onStart: noop,
  onComplete: noop,
  onSelect: noop,

  currentPositionStroke: "#2196F3",
  currentPositionStrokeOpacity: 1,
  currentPositionStrokeWidth: 2,
  currentPositionRadius: 4,
};

export const derivedLevelsHoverTextDefaults = {
  ...HoverTextNearMouse.defaultProps,
  enable: false,
  bgHeight: "auto",
  bgWidth: "auto",
  selectedText: "",
};

DerivedLevelsTool.propTypes = derivedLevelsPropTypes;

export default DerivedLevelsTool;
